import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import TrackStatusTabbedModal from "../../components/modals/asteraTrackStatus/trackStatusTabbedModal";
import NonEpaHistoryPanel from "../../components/modals/nonEpaModal/nonEpaHistoryPanel";
import NonEpaModal from "../../components/modals/nonEpaModal/nonEpaModal";
import PushToSftpModal from "../../components/modals/pushToSftpModal/pushToSftpModal";
import SftpStatus from "../../components/modals/SftpStatus/SftpStatus";
import { useTablesContext } from "../../context/tablesContextProvider";
import { ModalId } from "../../enums/modalId";
import { TableNames } from "../../enums/tableNames";
import { useModalOpener } from "../../hooks/useModalOpener";
import { closeModal } from "../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../redux/store/store";

interface CallingWorklistLayoutProps {
  /**
   * Table told to refetch after a non-EPA save. The Pending route passes its own
   * table so a save there refreshes the Pending list rather than the non-ePA one.
   */
  tableName?: TableNames;
}

/**
 * Layout component for the outreach worklist pages (non-ePA and Pending).
 * Renders the main outlet alongside the non-EPA modal that opens
 * when the Add/Edit button is clicked in the table.
 *
 * @param props.tableName - Table to refetch after a successful save
 */
const CallingWorklistLayout = ({
  tableName = TableNames.CALLING_WORKLIST_ORDERS,
}: CallingWorklistLayoutProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { metaData } = useSelector((state: RootState) => state.modalSliceNew);
  const { openedModalId } = useModalOpener();
  const { setTableDataForContext } = useTablesContext();

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  const handleSubmissionSuccess = () => {
    setTableDataForContext(tableName, {
      shouldRefetch: true,
    });
  };

  const isNonEpaModalOpen = openedModalId === ModalId.NON_EPA_MODAL;
  const isNonEpaHistoryOpen = openedModalId === ModalId.NON_EPA_HISTORY_PANEL;
  const nonEpaModalData =
    metaData && "data" in metaData ? (metaData as { data: any }).data : null;

  return (
    <div className="calling-worklist__container flex h-full w-full overflow-hidden">
      <div className="calling-worklist__outlet w-full flex-1">
        <Outlet />
      </div>
      {openedModalId === ModalId.PUSH_TO_SFTP_MODAL && <PushToSftpModal />}
      {openedModalId === ModalId.ASTERA_TRACK_STATUS_MODAL && (
        <TrackStatusTabbedModal
          onClose={() => {
            handleCloseModal();
            handleSubmissionSuccess();
          }}
          onSuccess={handleSubmissionSuccess}
        />
      )}
      {openedModalId === ModalId.SFTP_STATUS_MODAL && <SftpStatus />}
      {isNonEpaModalOpen && nonEpaModalData && (
        <NonEpaModal
          key={nonEpaModalData.identifier}
          data={nonEpaModalData}
          onClose={handleCloseModal}
          onSuccess={handleSubmissionSuccess}
        />
      )}
      {isNonEpaHistoryOpen && nonEpaModalData && (
        <NonEpaHistoryPanel
          key={nonEpaModalData.identifier}
          isOpen={isNonEpaHistoryOpen}
          data={nonEpaModalData}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default CallingWorklistLayout;
